import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Store, Truck, AlertTriangle, Package, Calendar, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * DeliveryTypeDialog - Visas vid utleverans för att välja typ
 * - "Såld via butiken" (store_sale)
 * - "Plock till runda" (route_pickup)
 */
export function DeliveryTypeDialog({ 
  open, 
  onOpenChange, 
  onSelect, 
  product,
  currentStock,
  bookedQuantity,
  hasBookings
}) {
  const [selectedType, setSelectedType] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [showOrderInput, setShowOrderInput] = useState(false);

  const handleSelect = (type) => {
    setSelectedType(type);
    // Always require order number for both delivery types
    setShowOrderInput(true);
  };

  const handleConfirm = () => {
    onSelect({ type: selectedType, orderNumber });
    resetDialog();
  };

  const resetDialog = () => {
    setSelectedType(null);
    setOrderNumber("");
    setShowOrderInput(false);
  };

  const handleCancel = () => {
    resetDialog();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Välj utleveranstyp
          </DialogTitle>
          {product && (
            <DialogDescription>
              {product.artikelnummer} - {product.produktnamn || product.ean}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Stock info */}
        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg text-sm">
          <div>
            <span className="text-muted-foreground">I lager:</span>
            <span className="font-bold ml-2">{currentStock} st</span>
          </div>
          {bookedQuantity > 0 && (
            <div>
              <span className="text-muted-foreground">Bokade:</span>
              <span className="font-bold ml-2 text-amber-500">{bookedQuantity} st</span>
            </div>
          )}
        </div>

        {!showOrderInput ? (
          <div className="space-y-3 py-4">
            <Card 
              className={cn(
                "p-4 cursor-pointer transition-all border-2",
                selectedType === "store_sale" 
                  ? "border-primary bg-primary/5" 
                  : "border-transparent hover:border-primary/50 hover:bg-muted/50"
              )}
              onClick={() => handleSelect("store_sale")}
              data-testid="delivery-type-store"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Store className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <h3 className="font-bold">Såld via butiken</h3>
                  <p className="text-sm text-muted-foreground">Direktförsäljning till kund i butik</p>
                </div>
              </div>
            </Card>

            <Card 
              className={cn(
                "p-4 cursor-pointer transition-all border-2",
                selectedType === "route_pickup" 
                  ? "border-primary bg-primary/5" 
                  : "border-transparent hover:border-primary/50 hover:bg-muted/50"
              )}
              onClick={() => handleSelect("route_pickup")}
              data-testid="delivery-type-route"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Truck className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-bold">Plock till runda</h3>
                  <p className="text-sm text-muted-foreground">Leverans via chaufför/runda</p>
                </div>
              </div>
            </Card>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
              {selectedType === "store_sale" ? (
                <Store className="h-5 w-5 text-green-500" />
              ) : (
                <Truck className="h-5 w-5 text-blue-500" />
              )}
              <span className="font-medium">
                {selectedType === "store_sale" ? "Såld via butiken" : "Plock till runda"}
              </span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="orderNumber">
                Ordernummer / Referens <span className="text-destructive">*</span>
              </Label>
              <Input
                id="orderNumber"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="T.ex. ORD-12345 eller kundnamn"
                autoFocus
                data-testid="delivery-order-input"
              />
              <p className="text-xs text-muted-foreground">
                Obligatoriskt för spårning i statistik och historik
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Avbryt
          </Button>
          {showOrderInput && (
            <Button 
              onClick={handleConfirm}
              disabled={!orderNumber.trim()}
              data-testid="delivery-confirm-btn"
            >
              Bekräfta utleverans
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * BookingBlockDialog - Visas när utleverans blockeras pga bokning
 */
export function BookingBlockDialog({
  open,
  onOpenChange,
  product,
  bookings,
  onCancelBooking,
  onForceDeliver
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            Produkt är bokad
          </DialogTitle>
          <DialogDescription>
            Denna produkt är bokad och kan inte skannas ut utan att avboka först.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {bookings?.map((booking) => (
            <Card key={booking.id} className="p-4 border-amber-500/30 bg-amber-500/5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{booking.comment || "Ingen kommentar"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(booking.created_at).toLocaleDateString("sv-SE")}</span>
                  </div>
                  <Badge variant="outline" className="mt-1">
                    {booking.quantity || 1} st bokade
                  </Badge>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-amber-500 border-amber-500/50"
                  onClick={() => onCancelBooking(booking)}
                >
                  Avboka
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt utleverans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ZeroStockDialog - Visas när utleverans blockeras pga saldo 0
 */
export function ZeroStockDialog({
  open,
  onOpenChange,
  product,
  location
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Utleverans blockerad
          </DialogTitle>
          <DialogDescription>
            Produkten har lagersaldo 0 och kan inte skannas ut.
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/30">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-destructive" />
            <div>
              <p className="font-bold">{product?.artikelnummer}</p>
              <p className="text-sm text-muted-foreground">{product?.produktnamn || product?.ean}</p>
              <p className="text-sm mt-1">
                <span className="text-muted-foreground">Lagerplats:</span> {location}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Saldo:</span>{" "}
                <span className="text-destructive font-bold">0 st</span>
              </p>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Du måste först skanna in produkten på lagerplatsen innan den kan skannas ut.
        </p>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
